import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import PropTypes from 'prop-types';

const ServiceCountry = ({ language = 'EN' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const controls = useAnimation();
    const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: false });

    // Language content
    const content = {
        EN: {
            title: "Our ASEAN Presence",
            countries: {
                "Malaysia": { x: 30, y: 72 },
                "Singapore": { x: 33, y: 81 },
                "Indonesia": { x: 27, y: 81 },
                "Thailand": { x: 28.6, y: 38 },
                "Philippines": { x: 63, y: 38 },
                "Cambodia": { x: 35, y: 48 },
                "Vietnam": { x: 44, y: 45 },
                "Laos": { x: 33, y: 27 },
                "Myanmar": { x: 20.8, y: 25 }
            }
        },
        BM: {
            title: "Kehadiran Kami di ASEAN",
            countries: {
                "Malaysia": { x: 30, y: 72 },
                "Singapura": { x: 33, y: 81 },
                "Indonesia": { x: 27, y: 81 },
                "Thailand": { x: 28.6, y: 38 },
                "Filipina": { x: 63, y: 38 },
                "Kemboja": { x: 35, y: 48 },
                "Vietnam": { x: 44, y: 45 },
                "Laos": { x: 33, y: 27 },
                "Myanmar": { x: 20.8, y: 25 }
            }
        },
        CN: {
            title: "我们在东盟的业务覆盖",
            countries: {
                "马来西亚": { x: 30, y: 72 },
                "新加坡": { x: 33, y: 81 },
                "印度尼西亚": { x: 27, y: 81 },
                "泰国": { x: 28.6, y: 38 },
                "菲律宾": { x: 63, y: 38 },
                "柬埔寨": { x: 35, y: 48 },
                "越南": { x: 44, y: 45 },
                "老挝": { x: 33, y: 27 },
                "缅甸": { x: 20.8, y: 25 }
            }
        }
    };

    const currentContent = content[language] || content.EN;
    const MAP_ASPECT_RATIO = 9 / 16;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.3 }
        }
    };

    const countryVariants = {
        hidden: { opacity: 0, scale: 0.5 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { type: "spring", stiffness: 100, damping: 10 }
        },
        hover: { scale: 1.1, transition: { duration: 0.2 } }
    };

    return (
        <Box
            id="service"
            ref={ref}
            sx={{
                position: 'relative',
                width: '100vw',
                height: `calc(100vw * ${MAP_ASPECT_RATIO})`,
                maxHeight: '100vh',
                overflow: 'hidden',
                left: '50%',
                transform: 'translateX(-50%)'
            }}>
            {/* Background Map */}
            <Box component={motion.div}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={controls}
                variants={{
                    hidden: { opacity: 0, scale: 1.1 },
                    visible: { opacity: 1, scale: 1 }
                }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: 'url(/images/map.png)',
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    filter: 'grayscale(30%)'
                }}
            />

            {/* Country Markers */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
            }}>
                {Object.entries(currentContent.countries).map(([country, pos]) => (
                    <Box
                        key={country}
                        component={motion.div}
                        variants={countryVariants}
                        whileHover="hover"
                        sx={{
                            position: 'absolute',
                            left: `${pos.x}%`,
                            top: `${pos.y}%`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{
                            width: isMobile ? 12 : 16,
                            height: isMobile ? 12 : 16,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 70%)`,
                            boxShadow: `0 0 0 4px rgba(217, 38, 45, 0.3), 0 0 20px ${theme.palette.primary.light}`,
                            mb: 1,
                            cursor: 'pointer'
                        }} />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                fontSize: isMobile ? '0.8rem' : '1rem',
                                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {country}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Content Container */}
            <Container
                maxWidth="xl"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 3
                }}
            >
                <motion.div
                    initial="hidden"
                    animate={controls}
                    variants={containerVariants}
                >
                    {/* Title Section */}
                    <Box
                        component={motion.div}
                        variants={{
                            hidden: { opacity: 0, y: -50 },
                            visible: { opacity: 1, y: 0 }
                        }}
                        sx={{
                            position: 'absolute',
                            top: '5%',
                            width: '100%',
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant={isMobile ? "h4" : "h2"}
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                            }}
                        >
                            {currentContent.title}
                        </Typography>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

ServiceCountry.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN'])
};

export default ServiceCountry;